import { SubmissionType } from '@common/enums/SubmissionType';
import { isEqual, isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { Box, Button, SplitButton, Stack } from '@common-components';
import { BrandingMode, FormMode, PlacementType, QuoteFileIdType, SubmissionStatus } from 'enums';
import { useToast } from 'hooks';
import { messages } from 'i18n';
import { getFirstErrorId, scrollToElement, triggerCustomError } from 'utils';
import { QuoteFormRoutes } from 'broker/broker-routes';
import { RouteKey } from 'broker/enums';
import { useNavigate, useNavigateBack, useNavigateToStep } from 'broker/hooks';
import { DetailsFieldsNames } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/form-methods/enums';
import { Subjectivity } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/form-methods/types';
import { FlowGenerationStatus } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/types';
import { useBrandConfig } from 'broker/pages/SubmissionWorkspacePage/contexts/BrandConfigProvider';
import { useStatusChangeDialog } from 'broker/pages/SubmissionWorkspacePage/dialogs/SubmissionStatusChangeDialog/useStatusChangeDialog';
import { useWorkspaceUrls } from 'broker/pages/SubmissionWorkspacePage/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { useUiStoreState } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';
import FooterStepper from './FooterStepper';
import { QuoteFormProps } from './types';
import { useGetQuoteSubmitOptions } from './useGetQuoteSubmitOptions';

export default function QuoteFooter(quoteFormProps: QuoteFormProps) {
  const {
    flowQuoteIds,
    mode,
    onClose,
    fileType,
    selectedQuote,
    currentStep,
    flowGenerationStatus,
    quoteConfig,
    submissionMarketRequestId,
    setIsDirty,
    selectedMarket,
    submitForm,
    setQuoteSubmitting,
    quoteSubmitting,
    submission,
  } = quoteFormProps;
  const quoteMethods = useFormContext();

  const { createBorPolicy } = useSubmissionsWorkspace();
  const { showStatusChangeDialog } = useStatusChangeDialog();

  const navigateToStep = useNavigateToStep();

  const { quoteStatusUrl } = useWorkspaceUrls();

  const navigate = useNavigate();

  const { getValues, trigger, formState } = quoteMethods;

  const [activeStep, setActiveStep] = useState(QuoteFormRoutes.Upload);

  const { filesExplorer } = useUiStoreState();

  const { showToast } = useToast();

  const navigateBack = useNavigateBack();

  const { isClientBranded, isLoading: loadingBrandConfig } = useBrandConfig();

  const isDirectPlacement = submission.placementType === PlacementType.Direct;
  const quoteFormSteps = quoteConfig.getFormSteps(isDirectPlacement);

  const handleChangeStep = (step: QuoteFormRoutes, direction: 'next' | 'prev', brandingMode = BrandingMode.Flow) => {
    navigateToStep(quoteFormSteps[quoteFormSteps.indexOf(step) + (direction === 'next' ? 1 : -1)], {
      routeKey: RouteKey.Quote,
      state: {
        brandingMode,
      },
    });
  };

  useEffect(() => {
    setActiveStep(currentStep);
  }, [currentStep]);

  const submitFormWrapper = async (isSave = false, saveAsDoc = false) => {
    const quoteId = await submitForm(saveAsDoc);
    if (quoteId && isSave) {
      showStatusChangeDialog({
        current: submission.status,
        newStatus: SubmissionStatus.Quoted,
      });
    }
    onClose();
  };

  const detailsExtraValidation = () => {
    const values = getValues();
    const isLimitEmpty = isNil(values[DetailsFieldsNames.Limit]) || values[DetailsFieldsNames.Limit] === '';
    const isAgentCommissionEmpty =
      isNil(values[DetailsFieldsNames.AgentCommission]) || values[DetailsFieldsNames.AgentCommission] === '';
    if (isLimitEmpty) {
      triggerCustomError(quoteMethods, DetailsFieldsNames.Limit, messages.addQuotePage.requiredFieldForFlowQuotePdf);
      return false;
    }
    if (isAgentCommissionEmpty && !submission?.isDemo) {
      triggerCustomError(
        quoteMethods,
        DetailsFieldsNames.AgentCommission,
        messages.addQuotePage.requiredFieldForFlowQuotePdf,
      );
      return false;
    }
    return true;
  };

  const submitStatusFiles = async () => {
    if (fileType === QuoteFileIdType.Policy && submission?.type === SubmissionType.BOR) {
      setQuoteSubmitting(true);
      try {
        await createBorPolicy(
          {
            submissionMarketRequestId: submissionMarketRequestId!,
            layerId: getValues(DetailsFieldsNames.AttachmentPoint)?.toString(),
            policyFileIds: filesExplorer.selectedFiles.map((selectedFile) => selectedFile.id),
            policyDetails: {
              policyNumber: getValues(DetailsFieldsNames.PolicyNumber),
              effectiveDate: getValues(DetailsFieldsNames.PolicyEffectiveDate)?.toString(),
              expirationDate: getValues(DetailsFieldsNames.PolicyExpirationDate)?.toString(),
            },
            flowCommission: getValues(DetailsFieldsNames.FlowCommission)
              ? parseFloat(getValues(DetailsFieldsNames.FlowCommission))
              : undefined,
          },
          selectedMarket?.id,
        );

        showToast('success', {
          message: messages.quoteStatusModal.binderPolicyIssued.successMessage(selectedMarket?.marketName || ''),
        });

        // Force React to flush the dirty flag update before we navigate away
        flushSync(() => {
          setIsDirty(false);
        });
        showStatusChangeDialog({
          current: submission!.status,
          newStatus: SubmissionStatus.Issued,
        });
        navigateBack();
      } finally {
        setQuoteSubmitting(false);
      }
      return;
    }

    navigate(quoteStatusUrl, {
      routeKey: RouteKey.QuoteStatus,
      state: {
        selectedQuote: selectedQuote!,
        uploadedFileIds: filesExplorer.selectedFiles.map((selectedFile) => selectedFile.id),
        policyDetails: {
          policyNumber: getValues(DetailsFieldsNames.PolicyNumber),
          // convert to string because we can't set a moment object in the location state
          effectiveDate: getValues(DetailsFieldsNames.PolicyEffectiveDate)?.toString(),
          expirationDate: getValues(DetailsFieldsNames.PolicyExpirationDate)?.toString(),
        },
        flowCommission: getValues(DetailsFieldsNames.FlowCommission)
          ? parseFloat(getValues(DetailsFieldsNames.FlowCommission))
          : undefined,
        fileType,
        binderSubjectivities: getValues(DetailsFieldsNames.BinderSubjectivities)?.map(
          (subjectivity: Subjectivity) => subjectivity.value,
        ),
        binderStateCompliance: getValues(DetailsFieldsNames.BinderStateCompliance),
        binderDate: getValues(DetailsFieldsNames.BinderDate)?.toString(),
      },
      replace: true,
    });
  };

  const withFormValidation = async (callback: () => void, extraValidation?: () => boolean) => {
    const response = await trigger();
    if (!response) {
      scrollToElement(getFirstErrorId(formState.errors));
      return;
    }
    if (extraValidation) {
      const isValid = extraValidation();
      if (!isValid) {
        return;
      }
    }

    callback();
  };

  const noSelectedFiles = filesExplorer.selectedFiles.length === 0;
  const isFinalStep = activeStep === QuoteFormRoutes.FlowQuote;

  // if undefined button should be enabled
  const disabledButtonText = useMemo(() => {
    if (
      quoteSubmitting ||
      (activeStep === QuoteFormRoutes.FlowQuote &&
        [FlowGenerationStatus.Loading, FlowGenerationStatus.Error].includes(flowGenerationStatus))
    ) {
      return '';
    }
    if (noSelectedFiles) {
      return messages.addQuotePage.noFilesSelectedTooltip;
    }
    return undefined;
  }, [activeStep, flowGenerationStatus, noSelectedFiles, quoteSubmitting]);

  const quoteSubmitOptions = useGetQuoteSubmitOptions({
    activeStep: currentStep,
    isFinalStep: currentStep === QuoteFormRoutes.FlowQuote,
    quoteFormSteps,
    flowQuoteIds,
    disabledButtonText,
    handleChangeStep,
    detailsExtraValidation,
    withFormValidation,
    submitFormWrapper,
    isClientBranded,
    isDirectPlacement,
  });

  function renderQuoteCTAs() {
    if (isDirectPlacement) {
      return (
        <Button
          variant="contained"
          onClick={() => withFormValidation(() => submitFormWrapper(true))}
          loading={quoteSubmitting}
          disabled={disabledButtonText !== undefined}
          disabledTooltipContent={disabledButtonText}
        >
          {messages.buttons.save}
        </Button>
      );
    }

    return (
      <Stack direction="row" gap={1}>
        {!isFinalStep && (
          <Button
            variant="outlined"
            onClick={() => withFormValidation(() => submitFormWrapper(true))}
            loading={quoteSubmitting}
            disabled={disabledButtonText !== undefined}
            disabledTooltipContent={disabledButtonText}
          >
            {messages.buttons.saveForLater}
          </Button>
        )}
        <SplitButton
          key={`quote-submit-${activeStep}`}
          config={{ options: quoteSubmitOptions }}
          variant="contained"
          loading={(isFinalStep && quoteSubmitting) || loadingBrandConfig}
          disabledTooltipContent={disabledButtonText}
        />
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap={1} justifyContent="center">
      <Box flex={1} />
      <Stack flex={1} alignItems="center">
        <FooterStepper handleChangeStep={handleChangeStep} activeStep={activeStep} mode={mode} steps={quoteFormSteps} />
      </Stack>
      <Stack flex={1} direction="row" gap={1} justifyContent="flex-end" alignItems="center">
        {/* Binder mode */}
        {fileType === QuoteFileIdType.Binder && mode !== FormMode.view && (
          <Button
            variant="contained"
            loading={quoteSubmitting}
            onClick={() => withFormValidation(submitStatusFiles)}
            disabled={
              filesExplorer.selectedFiles.length === 0 ||
              isEqual(filesExplorer.selectedFiles, filesExplorer.preSelectedFileIds)
            }
            disabledTooltipContent={
              isEqual(filesExplorer.selectedFiles, filesExplorer.preSelectedFileIds)
                ? messages.addQuotePage.selectDifferentFiles
                : messages.addQuotePage.noFilesSelectedTooltip
            }
          >
            {messages.addQuotePage.buttons.compareBinderToQuote}
          </Button>
        )}
        {/* Policy mode */}
        {fileType === QuoteFileIdType.Policy && mode !== FormMode.view && (
          <Button
            disabledTooltipContent={
              isEqual(filesExplorer.selectedFiles, filesExplorer.preSelectedFileIds)
                ? messages.addQuotePage.selectDifferentFiles
                : messages.addQuotePage.noFilesSelectedTooltip
            }
            variant="contained"
            loading={quoteSubmitting}
            onClick={() => withFormValidation(submitStatusFiles)}
            disabled={
              filesExplorer.selectedFiles.length === 0 ||
              isEqual(filesExplorer.selectedFiles, filesExplorer.preSelectedFileIds)
            }
          >
            {messages.addQuotePage.buttons.policy}
          </Button>
        )}
        {/* Quote mode */}
        {fileType === QuoteFileIdType.MarketQuote && mode !== FormMode.view && renderQuoteCTAs()}
      </Stack>
    </Stack>
  );
}
